import React, { useEffect, useLayoutEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import TdButton from './Button';

/** 워크보드 첫 진입 코치마크. 대상은 data-coach 속성으로 찾는다 */
export const COACH_STEPS = [
  {
    key: 'my-tasks',
    name: 'My tasks',
    text: 'New requests land in My tasks, and the badge shows how many you haven’t opened.',
    inMenu: true,
  },
  {
    key: 'task-card',
    name: 'Your tasks',
    text: 'Open a task to accept it, then submit your work from the same page.',
    inMenu: false,
    // All tasks 는 Timeline 으로 열려 카드가 없다 — My tasks 로 옮겨 카드를 가리킨다
    route: 'tasks/mine',
  },
  {
    key: 'checkpoints',
    name: 'Checkpoints',
    text: 'Check in here before each checkpoint. A badge shows when one is due.',
    inMenu: true,
  },
  {
    key: 'profile',
    name: 'Profile',
    text: 'Your working hours decide when you show as offline. You can change them anytime.',
    inMenu: true,
  },
  {
    key: 'complete-project',
    name: 'Complete project',
    text: 'Peer review opens here when your leader submits the final deliverable. You then have 3 hours to finish it, and offline time doesn’t count.',
    inMenu: true,
  },
  {
    // 6/6 — Resources 로 옮겨 가서 메뉴(모바일은 제목)와 Scenario 카드를 함께 가리킨다
    key: 'resources',
    mobileKey: 'resources-title',
    name: 'Resources',
    text: 'Files from the 2days team live here. Download them anytime.',
    second: {
      key: 'resources-scenario',
      name: 'Scenario',
      text: 'Open the scenario to see the situation and what to build. Read it before you start your first task.',
    },
    route: 'resources',
    inMenu: true,
  },
];

const GAP = 12;

const measure = (key) => {
  const el = [...document.querySelectorAll(`[data-coach="${key}"]`)].find((e) => {
    const r = e.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  });
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, right: r.right, bottom: r.bottom };
};

/** 같은 자리면 이전 값을 그대로 (불필요한 다시 그리기 방지) */
const sameRect = (a, b) =>
  a && b && a.top === b.top && a.left === b.left && a.right === b.right && a.bottom === b.bottom;

/** 대상 둘레 초록 링. scrim 이면 바깥을 어둡게 */
const Ring = ({ rect, scrim }) => (
  <div
    aria-hidden
    className='pointer-events-none fixed z-[1100] rounded-xl transition-all duration-200'
    style={{
      top: rect.top - 4,
      left: rect.left - 4,
      width: rect.right - rect.left + 8,
      height: rect.bottom - rect.top + 8,
      boxShadow: scrim
        ? '0 0 0 2px #1dc37b, 0 0 0 9999px rgba(23, 20, 16, 0.24)'
        : '0 0 0 2px #1dc37b',
    }}
  />
);

/** 말풍선. onSkip · onNext 가 없으면 버튼 줄을 그리지 않는다 */
const Tip = ({ label, text, pos, onSkip, onNext, nextLabel }) => (
  <div
    role='dialog'
    aria-label={`${label} tip`}
    className='fixed z-[1101] flex flex-col gap-2.5 rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00 px-4 pb-4 pt-3.5 shadow-popup-01'
    style={pos}
  >
    <div className='flex items-center justify-between gap-2'>
      <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>{label}</span>
      {onSkip && (
        <TdButton variant='ghost' size='s' onClick={onSkip} className='h-11 px-2.5 lg:h-8'>
          Skip
        </TdButton>
      )}
    </div>
    <p className='text-[14px] leading-[150%] text-text-primary [text-wrap:pretty]'>{text}</p>
    {onNext && (
      <div className='flex justify-end'>
        <TdButton
          variant='ghost'
          size='s'
          onClick={onNext}
          className='h-11 px-2.5 text-green-deep03 hover:text-green-deep04 lg:h-8'
        >
          {nextLabel}
        </TdButton>
      </div>
    )}
  </div>
);

/**
 * @param onEnter 단계가 바뀔 때 (step) — route 가 있으면 레이아웃이 그 화면으로 옮긴다
 */
const Coachmarks = ({ onDone, onNeedMenu, steps = COACH_STEPS, onEnter }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [rect2, setRect2] = useState(null);
  // 대상을 끝내 못 찾았을 때만 화면 아래에 띄운다 (그 전에는 아무것도 그리지 않는다)
  const [missing, setMissing] = useState(false);
  const [vw, setVw] = useState(window.innerWidth);
  const step = steps[index];
  const narrow = vw < 1024;
  // 좁은 화면에서 따로 가리킬 대상이 있으면(예: Resources 제목) 메뉴를 열지 않는다
  const targetKey = narrow && step.mobileKey ? step.mobileKey : step.key;
  const needMenu = narrow && step.inMenu && !step.mobileKey;

  useEffect(() => {
    onNeedMenu?.(needMenu);
  }, [needMenu, onNeedMenu]);

  useEffect(() => {
    onEnter?.(step);
  }, [step, onEnter]);

  // 배포 환경에서는 보드 데이터·메뉴가 늦게 그려져 대상이 처음엔 없다 — DOM 이 바뀔 때마다 다시 재고,
  // 찾기 전에는 말풍선을 띄우지 않는다 (아래 전체 너비로 떴다가 자리로 튀지 않게)
  useLayoutEffect(() => {
    let raf;
    const secondKey = step.second?.key;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setVw(window.innerWidth);
        const next = measure(targetKey);
        setRect((prev) => (sameRect(prev, next) ? prev : next));
        const next2 = secondKey ? measure(secondKey) : null;
        setRect2((prev) => (sameRect(prev, next2) ? prev : next2));
      });
    };
    setRect(measure(targetKey));
    setRect2(secondKey ? measure(secondKey) : null);
    setMissing(false);
    update();
    const late = setTimeout(update, 120);
    const giveUp = setTimeout(() => setMissing(true), 3000);
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(late);
      clearTimeout(giveUp);
      observer.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step, targetKey]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onDone();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDone]);

  const last = index === steps.length - 1;
  const next = () => (last ? onDone() : setIndex((i) => i + 1));
  const count = `${index + 1}/${steps.length}`;

  // 넓은 화면은 오른쪽, 좁은 화면은 위/아래에 표시
  const vh = window.innerHeight;
  let pos;
  if (!rect) pos = { left: 16, right: 16, bottom: 16 };
  else if (narrow)
    pos =
      rect.top > vh / 2
        ? { left: 16, right: 16, bottom: vh - rect.top + GAP }
        : { left: 16, right: 16, top: rect.bottom + GAP };
  else
    pos =
      rect.top > vh / 2
        ? { left: rect.right + GAP + 4, bottom: Math.max(16, vh - rect.bottom), width: 300 }
        : { left: rect.right + GAP + 4, top: Math.max(16, rect.top - 8), width: 300 };

  // 좁은 화면 — 첫 말풍선은 브리프 카드 위에 겹쳐 두고, 둘째는 그 바로 아래 (시안 P8 6/6 mobile)
  const stacked = narrow && step.second && rect2;
  if (stacked) pos = { left: 16, right: 16, top: rect2.top - 12 };
  // 두 번째 대상 — 넓은 화면은 카드 아래 오른쪽 맞춤
  const pos2 =
    rect2 &&
    (narrow
      ? { right: 16, width: Math.min(320, vw - 48), top: rect2.top + 88 }
      : {
          right: Math.max(16, window.innerWidth - rect2.right),
          top: rect2.bottom + GAP,
          width: 280,
        });

  if (!rect && !missing) return null;

  // 대상이 둘이면 첫 말풍선은 설명만, 둘째 말풍선에 Skip · Done(Next)
  const two = !!step.second && !!rect2;

  return ReactDOM.createPortal(
    <>
      {rect ? (
        <Ring rect={rect} scrim />
      ) : (
        <div aria-hidden className='fixed inset-0 z-[1100] bg-grayscale-gray10/[0.24]' />
      )}
      {two && <Ring rect={rect2} />}
      <Tip
        label={`${count} · ${step.name}`}
        text={step.text}
        pos={pos}
        onSkip={two ? null : onDone}
        onNext={two ? null : next}
        nextLabel={last ? 'Done' : 'Next'}
      />
      {two && (
        <Tip
          label={`${count} · ${step.second.name}`}
          text={step.second.text}
          pos={pos2}
          onSkip={onDone}
          onNext={next}
          nextLabel={last ? 'Done' : 'Next'}
        />
      )}
    </>,
    document.body,
  );
};

export default Coachmarks;
